import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Bell, BellOff, BellRing, Download, Smartphone, Monitor, Loader2, CheckCircle2 } from 'lucide-react';
import InstallPrompt, { getPlatformInfo } from './InstallPrompt';

// Same VAPID helper as PushNotificationManager
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

/**
 * Settings card exposing manual control of:
 *  - Push notifications (activate / test)
 *  - PWA install (mobile + desktop via beforeinstallprompt or instructions)
 *
 * Renders inside ProfilePage Notifications section.
 */
const NotificationsAndInstallSettings = () => {
  const { api } = useAuth();
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [platform, setPlatform] = useState({ isIOS: false, isAndroid: false, isStandalone: false, hasPrompt: false });

  useEffect(() => {
    setPlatform(getPlatformInfo());

    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setPushSupported(supported);
    if (!supported) return;

    setPermission(Notification.permission);

    navigator.serviceWorker.ready.then(async (registration) => {
      const sub = await registration.pushManager.getSubscription();
      if (sub) setPushSubscribed(true);
      try {
        const status = await api.get('/push/status');
        setPushSubscribed(status.data.subscribed);
      } catch (e) {
        // ignore
      }
    });
  }, [api]);

  const handleEnablePush = async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        toast.error('Permission refusée. Active les notifications dans les paramètres du navigateur.');
        return;
      }

      const { data } = await api.get('/push/vapid-public-key');
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
      await api.post('/push/subscribe', { subscription: subscription.toJSON() });

      setPushSubscribed(true);
      toast.success('Notifications activées !');
    } catch (err) {
      console.error('Push enable error:', err);
      toast.error('Erreur lors de l\'activation des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleTestPush = async () => {
    setTesting(true);
    try {
      await api.post('/push/test');
      toast.success('Notification test envoyée — vérifie ton écran');
    } catch (err) {
      toast.error('Impossible d\'envoyer la notification test');
    } finally {
      setTesting(false);
    }
  };

  const handleOpenInstall = () => setShowInstall(true);
  const handleCloseInstall = () => setShowInstall(false);

  return (
    <>
      <div className="space-y-3" data-testid="notifications-install-settings">
        {/* Push notifications row */}
        <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {pushSubscribed ? (
              <BellRing className="w-5 h-5 text-[var(--gold,#B5894A)] flex-shrink-0" />
            ) : permission === 'denied' ? (
              <BellOff className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            ) : (
              <Bell className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-medium">Notifications push (navigateur)</p>
              <p className="text-sm text-muted-foreground truncate">
                {!pushSupported && 'Non supporté par ce navigateur'}
                {pushSupported && permission === 'denied' && 'Bloquées — autorise-les dans les réglages du navigateur'}
                {pushSupported && permission !== 'denied' && pushSubscribed && 'Activées sur cet appareil'}
                {pushSupported && permission !== 'denied' && !pushSubscribed && 'Reçois nouveaux messages et événements en temps réel'}
              </p>
            </div>
          </div>
          {pushSupported && !pushSubscribed && permission !== 'denied' && (
            <Button
              size="sm"
              onClick={handleEnablePush}
              disabled={loading}
              data-testid="enable-push-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer'}
            </Button>
          )}
          {pushSubscribed && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestPush}
              disabled={testing}
              data-testid="test-push-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Tester'}
            </Button>
          )}
        </div>

        {/* Install PWA row */}
        <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {platform.isStandalone ? (
              <CheckCircle2 className="w-5 h-5 text-[var(--gold,#B5894A)] flex-shrink-0" />
            ) : /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? (
              <Smartphone className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            ) : (
              <Monitor className="w-5 h-5 text-muted-foreground flex-shrink-0" />
            )}
            <div className="min-w-0">
              <p className="font-medium">Installer l&apos;application</p>
              <p className="text-sm text-muted-foreground truncate">
                {platform.isStandalone
                  ? 'Application déjà installée sur cet appareil'
                  : platform.hasPrompt
                    ? 'Installe l\'app en un clic depuis ton bureau ou téléphone'
                    : platform.isIOS
                      ? 'Ouvre Safari → bouton Partager → "Sur l\'écran d\'accueil"'
                      : 'Ouvre dans Chrome ou Edge pour pouvoir installer l\'app'}
              </p>
            </div>
          </div>
          {!platform.isStandalone && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleOpenInstall}
              data-testid="install-app-btn"
            >
              <Download className="w-4 h-4 mr-1.5" />
              Installer
            </Button>
          )}
        </div>
      </div>

      {showInstall && <InstallPrompt forceShow onClose={handleCloseInstall} />}
    </>
  );
};

export default NotificationsAndInstallSettings;
